import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, AttributionControl, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { makeStyles } from '@material-ui/styles';
import LayerControl, { GroupedLayer } from '../LayerControls/LayerControl';
import MiniMapComponent from "./MiniMap.component";
import BaseMapLayerProvider from "./BaseMapLayerProvider.component";
import PlacePopupComponent from "./PlacePopup.component";
import L from 'leaflet';
import { translations } from "../../i18n/i18n";

require('leaflet/dist/leaflet.css');
require('react-leaflet-markercluster/dist/styles.min.css');

// Function for creating custom icon for cluster group
// https://github.com/Leaflet/Leaflet.markercluster#customising-the-clustered-markers
// NOTE: iconCreateFunction is running by leaflet, which is not support ES6 arrow func syntax
// eslint-disable-next-line
const createClusterCustomIcon = function (cluster) {
	return L.divIcon({
		html: `<span>\${cluster.getChildCount()}</span>`,
		className: 'marker-cluster-custom',
		iconSize: L.point(40, 40, true),
	});
};
/*.marker-cluster-custom {
  background: #9370db;
  border: 3px solid #ededed;
  border-radius: 50%;
  color: #ededed;
  height: 40px;
  line-height: 37px;
  text-align: center;
  width: 40px;
}*/

const useStyles = makeStyles({
	placeTypeIcon: {
		background: '#fff',
		borderRadius: '50%',
		outline: '1px solid #254e918a !important',
		padding: 7,
	},
});

const mapBounds = [
	[49.117162, 11.229890],
	[44.142944, 26.830963],
];
const mapCenter = [46.96479, 18.13974];

const PlacesWatcher = ({ numberOfPlaces }) => {
	const map = useMap();
	
	useEffect(() => {
		if(numberOfPlaces < 10 && map.getZoom() > 8) {
			//Probably search, reset map to make sure search results are visible
			map.panTo(mapCenter);
			map.setZoom(8);
		}
	}, [ numberOfPlaces ]);

	return null;
};

const Map = ({ placeTypes, onInitialized, placesToShow, notifyAboutCurrentLayer }) => {
	const classes = useStyles();

	//Create leaflet icons for each place type
	const placeTypeIcons = useMemo(() => {
		let output = {};
		for(const placeType of placeTypes) {
			output[placeType.id] = L.divIcon({
				html: placeType.icon,
				className: classes.placeTypeIcon,
				iconSize: [20, 20],
				iconAnchor: [20, 20],
				popupAnchor: [-2, -19],
			});
		}

		return output;
	}, [placeTypes]);

	return <MapContainer preferCanvas={true} style={{flexGrow: 1, height: '100%'}} center={mapCenter} zoom={8} whenReady={() => setTimeout(onInitialized, 1000)} minZoom={7} maxZoom={process.env.REACT_APP_MAX_ZOOM} maxBounds={mapBounds} attributionControl={false}>
		<MiniMapComponent position="bottomleft" />
		{process.env.REACT_APP_HIDE_MAP_COPYRIGHT !== '1' &&
			<AttributionControl position="bottomright" prefix='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>, <a href="https://dh.gu.se/#/resource/6" target="_blank">Johan Åhlfeldt - DARE</a>' />}

		<BaseMapLayerProvider notifyAboutCurrentLayer={notifyAboutCurrentLayer} />

		<PlacesWatcher numberOfPlaces={placesToShow.length} />

		<LayerControl position="topright">
			<GroupedLayer name="Modern" group={translations.components.map.mapLayer}>
				<TileLayer
					attribution=''
					url={process.env.REACT_APP_CUSTOM_CDN_URL ? process.env.REACT_APP_CUSTOM_CDN_URL+"/osm/{z}/{x}/{y}.png" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
				/>
			</GroupedLayer>
		</LayerControl>

		<MarkerClusterGroup disableClusteringAtZoom={process.env.REACT_APP_MAX_ZOOM} /*iconCreateFunction={createClusterCustomIcon}*/>
			{placesToShow.map(place => <Marker key={place.id} icon={placeTypeIcons[place.place_type_ids[0]]} position={[place.coordinate_y, place.coordinate_x]}>
				<PlacePopupComponent place={place} placeTypes={placeTypes} placeTypeIcons={placeTypeIcons} />
				<Tooltip direction="top" offset={[-2, -24]}>{place.name}</Tooltip>
			</Marker>)}
		</MarkerClusterGroup>
	</MapContainer>;
}

export default React.memo(Map);